import { Component, OnInit, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { CookieService, CookieConsent } from '../../services/cookie.service';
import { Router } from '@angular/router';
import { TranslatePipe } from '../../pipes/translate.pipe';

@Component({
  selector: 'app-cookie-consent',
  templateUrl: './cookie-consent.component.html',
  styleUrls: ['./cookie-consent.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  animations: [
    trigger('slideInOut', [
      state('in', style({ 
        opacity: 1, 
        transform: 'translateY(0)',
        maxHeight: '80vh'
      })),
      transition('void => *', [
        style({ 
          opacity: 0, 
          transform: 'translateY(-20px)',
          maxHeight: '0'
        }),
        animate('300ms ease-in-out')
      ]),
      transition('* => void', [
        animate('300ms ease-in-out', style({ 
          opacity: 0, 
          transform: 'translateY(-20px)',
          maxHeight: '0'
        }))
      ])
    ])
  ]
})
export class CookieConsentComponent implements OnInit, AfterViewInit {
  @ViewChild('cookieDetails') cookieDetailsRef?: ElementRef;
  
  isVisible = false;
  showDetails = false;
  
  cookieConsent: CookieConsent = {
    necessary: true, // Always true and disabled
    functional: false,
    analytics: false,
    marketing: false
  };

  constructor(
    private cookieService: CookieService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Check if user has already given consent
    if (!this.cookieService.hasConsent() || this.cookieService.isConsentExpired()) {
      // Show banner after a short delay for better UX
      setTimeout(() => {
        this.isVisible = true;
      }, 1000);
    } else {
      // Load existing preferences
      const consent = this.cookieService.getConsent();
      if (consent) {
        this.cookieConsent = consent;
      }
    }
  }

  ngAfterViewInit(): void {
    // Setup scroll detection for the details section
    this.setupScrollDetection();
  }

  private setupScrollDetection(): void {
    if (this.cookieDetailsRef) {
      const element = this.cookieDetailsRef.nativeElement;
      
      // Check if content is scrollable and add class accordingly
      const checkScrollable = () => {
        if (element.scrollHeight > element.clientHeight) {
          element.classList.add('scrollable');
        } else {
          element.classList.remove('scrollable');
        }
      };
      
      // Initial check
      setTimeout(checkScrollable, 100);
      
      // Check on window resize
      window.addEventListener('resize', checkScrollable);
    }
  }

  acceptAll(): void {
    this.cookieConsent = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    this.saveConsent();
  }

  acceptNecessary(): void {
    this.cookieConsent = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    };
    this.saveConsent();
  }

  savePreferences(): void {
    this.saveConsent();
  }

  private saveConsent(): void {
    this.cookieService.updateConsent(this.cookieConsent);
    this.isVisible = false;
    this.showDetails = false;
    
    // Initialize services based on consent
    this.initializeServices();
  }

  private initializeServices(): void {
    // Enable/disable services based on consent
    if (this.cookieConsent.analytics) {
      // Initialize analytics (Google Analytics, etc.)
              // Debug log removed
      this.initializeGoogleAnalytics();
    }
    
    if (this.cookieConsent.marketing) {
      // Initialize marketing cookies (Facebook Pixel, etc.)
              // Debug log removed
      this.initializeMarketingPixels();
    }
    
    if (this.cookieConsent.functional) {
      // Initialize functional cookies (Chat widgets, etc.)
              // Debug log removed
      this.initializeFunctionalFeatures();
    }
  }

  private initializeGoogleAnalytics(): void {
    // Add Google Analytics initialization here
    // Example:
    // gtag('config', 'GA_TRACKING_ID');
  }

  private initializeMarketingPixels(): void {
    // Add Facebook Pixel, Google Ads, etc. initialization here
  }

  private initializeFunctionalFeatures(): void {
    // Add chat widgets, preference tracking, etc.
  }

  toggleDetails(): void {
    this.showDetails = !this.showDetails;
    
    // Setup scroll detection when details are shown
    if (this.showDetails) {
      setTimeout(() => {
        this.setupScrollDetection();
      }, 350); // Wait for animation to complete
    }
  }

  openPrivacyPolicy(): void {
    // Navigate to privacy policy
    this.router.navigate(['/legal/privacy-policy']);
  }
}
